import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient, useReactiveVar } from '@apollo/client';

import {
  ExpensesDocument,
  ExpensesQuery,
  ExpensesQueryVariables,
} from 'core/graphql';

import { IExpenseListRow, IExpensesFilters } from './types';
import { isPermissionDeniedError } from './errors';
import { financeListsVersion } from './evictFinanceLists';

interface IAllExpensesHook {
  data: IExpenseListRow[];
  loading: boolean;
  fetching: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

const BATCH_SIZE = 200;

/**
 * Sibling of `useAllFakturowniaInvoices` for the Expense list. Walks every
 * page of `expenses` until the backend returns a short page; consumers get
 * a flat array of every row that matches the filter. Phase 9.1: replaces
 * the page-capped `useExpenses` in views that wanted «всё в выборке»
 * semantics — Expense register, GroupDetail Finances, TeacherDetail Payouts.
 */
export const useAllExpenses = (filters: IExpensesFilters): IAllExpensesHook => {
  const client = useApolloClient();
  const [rows, setRows] = useState<IExpenseListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const [refetchTick, setRefetchTick] = useState(0);
  // Explicit refetch() forces the network; ordinary walks serve already-seen
  // pages from the cache (tab revisits stop re-downloading — spec 0008 r2).
  const forceNetworkRef = useRef(false);

  const baseVariables: Omit<
    ExpensesQueryVariables,
    'page' | 'perPage'
  > = useMemo(
    () => ({
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      source: filters.source || undefined,
      categorySlug: filters.categorySlug || undefined,
      recurrence: filters.recurrence || undefined,
      status: filters.status || undefined,
      teacherId: filters.teacherId || undefined,
      groupId: filters.groupId || undefined,
      legalEntityId: filters.legalEntityId || undefined,
    }),
    [
      filters.dateFrom,
      filters.dateTo,
      filters.source,
      filters.categorySlug,
      filters.recurrence,
      filters.status,
      filters.teacherId,
      filters.groupId,
      filters.legalEntityId,
    ],
  );

  const variablesKey = JSON.stringify(baseVariables);

  // Re-walk when a money mutation / sync evicts the drained field (finance-
  // block audit) — see useAllFakturowniaInvoices.
  const listsVersion = useReactiveVar(financeListsVersion);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    // Phase 11: stale-while-revalidate — DON'T blank `rows` here. The page
    // dims the previous list while the next set streams in; wiping early
    // would trigger the full-screen preloader on every filter change.

    const fetchAll = async () => {
      const accumulator: IExpenseListRow[] = [];
      // Same boundary-row dedup as useAllFakturowniaInvoices (audit).
      const seenIds = new Set<string>();
      let page = 1;
      try {
        // Sequential by design: we only know if a next page exists after
        // the current one returns. `no-await-in-loop` doesn't apply.
        // eslint-disable-next-line no-constant-condition, no-await-in-loop
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const result = await client.query<
            ExpensesQuery,
            ExpensesQueryVariables
          >({
            query: ExpensesDocument,
            variables: { ...baseVariables, page, perPage: BATCH_SIZE },
            fetchPolicy: forceNetworkRef.current
              ? 'network-only'
              : 'cache-first',
          });
          if (cancelled) return;

          const batch = (
            result.data?.expenses || []
          ).filter((r): r is IExpenseListRow => Boolean(r));
          batch.forEach((r) => {
            if (!seenIds.has(r.id)) {
              seenIds.add(r.id);
              accumulator.push(r);
            }
          });
          setRows(accumulator.slice());

          // Termination checks the RAW page size, not the deduped count.
          if (batch.length < BATCH_SIZE) break;
          page += 1;
        }
      } catch (e) {
        if (!cancelled) setError(e as ApolloError);
      } finally {
        if (!cancelled) {
          setLoading(false);
          forceNetworkRef.current = false;
        }
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesKey, refetchTick, listsVersion, client]);

  const refetch = useCallback(() => {
    forceNetworkRef.current = true;
    setRefetchTick((t) => t + 1);
  }, []);

  return {
    data: rows,
    loading,
    fetching: loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch,
  };
};
